import { withDependencies, named } from '@wix/thunderbolt-ioc'
import type { IPageChildMeasures, ComponentsMeasureEditorConfig } from './types'
import { name } from './symbols'
import {
	ViewerManagerStructureAPISymbol,
	EditorFeatureConfigSymbol,
	ILogger,
	LoggerSymbol,
} from '@wix/thunderbolt-symbols'
import { ViewerAPI } from 'thunderbolt-viewer-manager-types'
import { Component } from '@wix/thunderbolt-becky-root'
import _ from 'lodash'

const PAGE_COMP_TYPE = 'mobile.core.components.Page'
const SKINS_TO_BORDER_WIDTH: { [skinName: string]: number } = { SloopyPageSkin: 3 }

const getParent = (compId: string, structureViewerApi: ViewerAPI['structure']) => {
	const parentId = structureViewerApi.getStructure(compId)?.parent
	if (!parentId) {
		return {} as Component
	}

	return structureViewerApi.getStructure(parentId)
}

const pageChildMeasuresFactory = (
	structureViewerApi: ViewerAPI['structure'],
	config: ComponentsMeasureEditorConfig,
	logger: ILogger
): IPageChildMeasures => {
	const getPageSkinBorderWidth = (pageId: string) => {
		const pageSkin = config.pagesSkin[pageId] || ''
		return SKINS_TO_BORDER_WIDTH[pageSkin]
	}

	const shouldUseCustomMeasureForPageChild: IPageChildMeasures['shouldUseCustomMeasureForPageChild'] = (compId) => {
		const parent = getParent(compId, structureViewerApi)
		if (!parent) {
			logger.captureError(new Error(`parent structure missing`), {
				tags: { feature: 'PageChildMeasures' },
				extra: {
					child: {
						compId,
						componentType: structureViewerApi.getStructure(compId)?.componentType,
					},
					parentId: structureViewerApi.getStructure(compId)?.parent,
					errorMessage: 'parent structure missing',
				},
			})
		}
		return parent.componentType === PAGE_COMP_TYPE && _.isNumber(getPageSkinBorderWidth(parent.id))
	}

	const getMeasuresForPageChild: IPageChildMeasures['getMeasuresForPageChild'] = (
		compId,
		measureType,
		measuresApi,
		measureMap,
		customMeasure
	) => {
		const pageId = getParent(compId, structureViewerApi).id!
		const pageSkinBorderWidth = getPageSkinBorderWidth(pageId) || 0

		if (_.isNumber(customMeasure)) {
			return customMeasure - pageSkinBorderWidth
		}

		const element = measuresApi.getCompElement(compId)!
		if (measureType === 'left') {
			return measuresApi.getCompLeft(element, compId, measureMap) - pageSkinBorderWidth
		}

		if (measureType === 'top') {
			return measuresApi.getCompTop(element, compId, measureMap) - pageSkinBorderWidth
		}

		return customMeasure
	}

	return {
		shouldUseCustomMeasureForPageChild,
		getMeasuresForPageChild,
	}
}

export const PageChildMeasures = withDependencies(
	[ViewerManagerStructureAPISymbol, named(EditorFeatureConfigSymbol, name), LoggerSymbol],
	pageChildMeasuresFactory
)
